// BankTransactionsPage.js — Internal Bank Accounts
// ─────────────────────────────────────────────────────────────────────────────
// A query tool over every transaction the Wells Fargo sync has swept in, across
// every account. Not an analytics page pretending to be a query tool: the
// figures at the top exist to tell you whether your filter caught what you
// meant, and everything else is the grid.
//
// IT IS NOT THE PMT POSTING SCREEN, and the difference is the point. That
// screen reads dbo.vWFTransactions, which is scoped to the collection accounts,
// because a credit into an operating account is not a loan payment. This reads
// the base table, so the sweeps, fees, investment transfers and inter-company
// book transfers that screen correctly hides are exactly what you see here.
//
// NOTHING HERE POSTS, MATCHES OR EDITS. Posting a payment happens on the Pmt
// Posting screen, where the rules and the audit trail live. A read-only tool
// that quietly grew a write button would be two places to post from and one
// place to look when a posting is wrong.
//
// ACCOUNT NUMBERS ARE MASKED END TO END. The server sends the last four and
// never the number; the filter sends the last four back. The wire text is
// masked too — the full text of one row is fetched on demand when somebody
// expands it, so unmasked counterparty details travel one row at a time and
// only when asked for.

import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { B, INK_MUTED, INK_PRIMARY, SURFACE_MUTED,
         HAIRLINE, HAIRLINE_FIRM } from "../../../shared/theme";
import { Card, Spinner, ErrorMsg, Btn, FilterSelect } from "../../../shared/ui";
import { FilterField, Multi, DateRange, DATE_PRESETS, AmountRange, Segmented,
         filterInput } from "../../../shared/filters";
import { exportCSV } from "../../../shared/format";
import { useApp } from "../../../shared/appContext";

const PAGE_SIZE = 100;

const num = v => { const n = parseFloat(v); return isFinite(n) ? n : 0; };
const moneyFull = v => (v == null ? "—"
  : (num(v) < 0 ? "-" : "") + "$" + Math.abs(num(v)).toLocaleString("en-US",
      { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const moneyShort = v => {
  const n = num(v), a = Math.abs(n), s = n < 0 ? "-" : "";
  if (a >= 1e9) return `${s}$${(a / 1e9).toFixed(1)}B`;
  if (a >= 1e6) return `${s}$${(a / 1e6).toFixed(1)}MM`;
  if (a >= 1e3) return `${s}$${(a / 1e3).toFixed(0)}K`;
  return `${s}$${a.toFixed(0)}`;
};
const usDate = d => {
  const m = String(d || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${Number(m[2])}/${Number(m[3])}/${m[1]}` : (d || "—");
};
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const periodLabel = p => {
  const m = String(p || "").match(/^(\d{4})-(\d{2})$/);
  return m ? `${MONTHS[Number(m[2]) - 1]} ${m[1].slice(2)}` : String(p || "");
};

// WHERE THE PAGE OPENS. The two collection accounts, money coming in, already
// posted — which is the question this gets opened for most of the time, and the
// one that would otherwise be reassembled from four controls on every visit.
//
// Every one of these is a normal filter, not a hidden floor: they show in the
// controls, "Clear" empties them like any other, and the two operating accounts
// are one click away in the Account list. A default you cannot see is the kind
// that gets reconciled against and then cannot be reproduced.
const DEFAULTS = {
  accounts:  ["8952", "9966"],   // the collection accounts, by last four
  direction: "CREDIT",
  posted:    "1",
};

// Opens on the last 90 days rather than on everything. The table is appended to
// daily and forever, so "no filter" is a query that gets slower every day and
// answers a question nobody asked.
const openingRange = () => {
  const t = new Date(); const s = new Date(t); s.setUTCDate(s.getUTCDate() - 90);
  return [s.toISOString().slice(0, 10), t.toISOString().slice(0, 10)];
};

export const BankTransactionsPage = () => {
  const { api, role } = useApp();
  const isAdmin = !!(role && role.isAdmin);

  const [open0, open1] = useMemo(openingRange, []);
  const [dateFrom, setDateFrom] = useState(open0);
  const [dateTo, setDateTo]     = useState(open1);
  const [account, setAccount]   = useState(DEFAULTS.accounts);
  const [direction, setDirection] = useState(DEFAULTS.direction);   // "" | CREDIT | DEBIT
  const [type, setType]         = useState([]);
  const [bai, setBai]           = useState([]);
  const [status, setStatus]     = useState([]);
  const [posted, setPosted]     = useState(DEFAULTS.posted);        // "" | "1" | "0"
  const [amtMin, setAmtMin]     = useState("");
  const [amtMax, setAmtMax]     = useState("");
  const [search, setSearch]     = useState("");
  const [applied, setApplied]   = useState("");

  const [sort, setSort] = useState({ key: "date", dir: "desc" });
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState(null);      // { id, row, busy, err }

  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [err, setErr]   = useState("");
  const [exporting, setExporting] = useState(false);

  const params = useMemo(() => {
    const p = { page, pageSize: PAGE_SIZE, sort: sort.key, dir: sort.dir };
    const iso = v => (/^\d{4}-\d{2}-\d{2}$/.test(String(v || "").trim()) ? v.trim() : null);
    if (iso(dateFrom)) p.dateFrom = iso(dateFrom);
    if (iso(dateTo))   p.dateTo   = iso(dateTo);
    if (account.length) p.account = account.join(",");
    if (direction)      p.direction = direction;
    if (type.length)    p.type   = type.join(",");
    if (bai.length)     p.bai    = bai.join(",");
    if (status.length)  p.status = status.join(",");
    if (posted !== "")  p.posted = posted;
    const money = v => { const n = parseFloat(String(v).replace(/[$,\s]/g, ""));
                         return isFinite(n) && n >= 0 ? n : null; };
    if (money(amtMin) !== null) p.minAmount = money(amtMin);
    if (money(amtMax) !== null) p.maxAmount = money(amtMax);
    if (applied) p.search = applied;
    return p;
  }, [dateFrom, dateTo, account, direction, type, bai, status, posted,
      amtMin, amtMax, applied, sort.key, sort.dir, page]);
  const key = JSON.stringify(params);

  // Sequenced, so a slow answer to an old filter cannot overwrite the answer to
  // the current one. These filters change fast and a stale write would show
  // numbers belonging to a selection no longer on screen.
  const seq = useRef(0);
  const load = useCallback(() => {
    const mine = ++seq.current;
    setBusy(true); setErr("");
    return api.get("getBankTransactions", params)
      .then(d => { if (mine === seq.current) { setData(d); setBusy(false); } })
      .catch(e => { if (mine === seq.current) { setErr(e.message); setBusy(false); } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, key]);
  useEffect(() => { load(); }, [load]);

  // Any change to the SELECTION resets to the first page; a change to the PAGE
  // obviously must not. Without this, narrowing a filter while on page 4 lands
  // on an empty page and reads as "no results".
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(0); setExpanded(null); },
    [dateFrom, dateTo, account.join(","), direction, type.join(","), bai.join(","),
     status.join(","), posted, amtMin, amtMax, applied, sort.key, sort.dir]);

  const f = (data && data.filters) || { accounts: [], types: [], bais: [], statuses: [] };
  const t = (data && data.totals) || { rows: 0, credits: 0, debits: 0,
                                       creditAmount: 0, debitAmount: 0, net: 0, unposted: 0 };
  const rows = (data && data.rows) || [];
  const paging = (data && data.paging) || { page: 0, total: 0, more: false };

  const anyFilter = account.length || direction || type.length || bai.length
                 || status.length || posted !== "" || amtMin || amtMax || applied;
  const clearAll = () => {
    setAccount([]); setDirection(""); setType([]); setBai([]); setStatus([]);
    setPosted(""); setAmtMin(""); setAmtMax(""); setSearch(""); setApplied("");
  };
  const restoreDefaults = () => {
    setAccount(DEFAULTS.accounts); setDirection(DEFAULTS.direction);
    setPosted(DEFAULTS.posted);
    setType([]); setBai([]); setStatus([]);
    setAmtMin(""); setAmtMax(""); setSearch(""); setApplied("");
  };
  // Compared as a SET, not as a joined string: ["9966","8952"] is the same
  // selection as ["8952","9966"], and picking the two accounts back in a
  // different order should not leave the button claiming they differ.
  const atDefaults =
    account.length === DEFAULTS.accounts.length
    && DEFAULTS.accounts.every(a => account.includes(a))
    && direction === DEFAULTS.direction && posted === DEFAULTS.posted
    && !type.length && !bai.length && !status.length
    && !amtMin && !amtMax && !applied;

  const onSort = (k) => setSort(s => (s.key === k
    ? { key: k, dir: s.dir === "asc" ? "desc" : "asc" }
    : { key: k, dir: k === "date" || k === "amount" ? "desc" : "asc" }));

  // THE EXPORT IS THE FILTERED SET, NOT THE PAGE. Exporting what happens to be
  // on screen is the classic quiet wrong answer — the file looks complete and
  // is one hundred rows of a thousand.
  const runExport = async () => {
    setExporting(true);
    try {
      const d = await api.get("getBankTransactions", { ...params, view: "export" });
      exportCSV((d.rows || []).map(r => ({
        date: r.date, account: r.acct, accountName: r.accountName,
        direction: r.direction, amount: r.amount, type: r.type,
        code: r.baiLabel, status: r.status, posted: r.posted ? "Yes" : "No",
        company: r.company || "", loan: r.loanNumber || "",
        reference: r.reference || "", description: r.description || "",
      })), ["date", "account", "accountName", "direction", "amount", "type", "code",
            "status", "posted", "company", "loan", "reference", "description"],
        "internal-bank-transactions.csv");
      if (d.paging && d.paging.truncated) {
        setErr(`The export stopped at ${d.paging.cap.toLocaleString("en-US")} rows of `
             + `${d.paging.total.toLocaleString("en-US")}. Narrow the dates and export again.`);
      }
    } catch (e) { setErr(e.message); }
    setExporting(false);
  };

  // The raw wire text for ONE row, fetched only when somebody opens it. This is
  // the only path that carries unmasked counterparty detail, and it carries one
  // row that a person asked for by name.
  const toggleRow = async (r) => {
    if (expanded && expanded.id === r.id) { setExpanded(null); return; }
    setExpanded({ id: r.id, row: null, busy: true, err: "" });
    try {
      const d = await api.get("getBankTransactions", { view: "detail", id: r.id });
      setExpanded(x => (x && x.id === r.id ? { ...x, row: d.row, busy: false } : x));
    } catch (e) {
      setExpanded(x => (x && x.id === r.id ? { ...x, busy: false, err: e.message } : x));
    }
  };

  if (!isAdmin) {
    return (
      <div style={{ padding: 16 }}>
        <h2 style={{ margin: 0, color: INK_PRIMARY }}>Internal Bank Accounts</h2>
        <Card style={{ marginTop: 12 }}>
          <div style={{ color: INK_MUTED, fontSize: 13 }}>
            This tool reads the firm's operating and collection accounts and is
            available to administrators. The Pmt Posting screen has the collection
            account activity.
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0, color: INK_PRIMARY }}>Internal Bank Accounts</h2>
        <span style={{ color: INK_MUTED, fontSize: 13 }}>
          Every account the bank sync sweeps in — read only.
        </span>
        <span style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <Btn onClick={runExport} disabled={exporting || !t.rows}>
            {exporting ? "Exporting…" : "Export CSV"}</Btn>
          <Btn onClick={load} disabled={busy}>Refresh</Btn>
        </span>
      </div>

      {err ? <ErrorMsg msg={err} onRetry={load} /> : null}

      <Card style={{ marginTop: 12, padding: 12 }}>
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center" }}>
          <DateRange from={dateFrom} to={dateTo} setFrom={setDateFrom} setTo={setDateTo}
                     presets={DATE_PRESETS} />
          <Multi label="Account" value={account} setValue={setAccount} allLabel="All accounts"
                 width={150}
                 options={f.accounts.map(a => ({ value: a.value, label: a.label,
                                                 note: a.name }))} />
          <FilterSelect label="Direction" value={direction} onChange={setDirection}
                        allLabel="Credits & debits"
                        options={[{ value: "CREDIT", label: "Credits" },
                                  { value: "DEBIT",  label: "Debits" }]} />
          <Multi label="Type" value={type} setValue={setType} allLabel="All types"
                 options={f.types} />
          <Multi label="BAI code" value={bai} setValue={setBai} allLabel="All codes"
                 width={170}
                 options={f.bais.map(x => ({ value: x.value, label: x.label, note: x.n }))} />
          <Multi label="Status" value={status} setValue={setStatus} allLabel="All statuses"
                 options={f.statuses} />
          <Segmented label="Posted" value={posted} setValue={setPosted}
                     options={[{ value: "", label: "Any" },
                               { value: "1", label: "Posted" },
                               { value: "0", label: "Unposted" }]} />
          <AmountRange min={amtMin} max={amtMax} setMin={setAmtMin} setMax={setAmtMax} />
          <form onSubmit={e => { e.preventDefault(); setApplied(search.trim()); }}
                style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <FilterField label="Search" as="label">
              <input value={search} onChange={e => setSearch(e.target.value)}
                     placeholder="Company, loan, reference, wire text…"
                     style={{ ...filterInput, minWidth: 250 }} />
            </FilterField>
            <Btn>Search</Btn>
          </form>
          {anyFilter ? <Btn onClick={clearAll}>Clear</Btn> : null}
          {atDefaults ? null : (
            <Btn onClick={restoreDefaults} title="Collection accounts, credits, posted">
              Default view</Btn>
          )}
        </div>
      </Card>

      {/* THE FIGURES ARE OVER THE WHOLE FILTERED SET, not the page — which is
          why they can be reconciled against and why they do not move when you
          click Next. */}
      <div style={{ display: "grid", gap: 12, marginTop: 12,
                    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}>
        <Stat label="Transactions" value={t.rows.toLocaleString("en-US")}
              note={t.earliest ? `${usDate(t.earliest)} – ${usDate(t.latest)}` : null} />
        <Stat label="Credits" value={moneyShort(t.creditAmount)}
              note={`${t.credits.toLocaleString("en-US")} in`} />
        <Stat label="Debits" value={moneyShort(t.debitAmount)}
              note={`${t.debits.toLocaleString("en-US")} out`} />
        {/* Credits less debits over the selection. NOT a balance — the sync
            carries transactions, not statement balances, so this is the net
            movement across whatever you filtered to and nothing more. */}
        <Stat label="Net movement" value={moneyShort(t.net)}
              note="credits less debits — not a balance" />
      </div>

      {busy && !data ? <Spinner /> : null}

      {data && data.byAccount && data.byAccount.length > 1 ? (
        <Card style={{ marginTop: 12 }}>
          <div style={{ fontWeight: 600, color: INK_PRIMARY, marginBottom: 2 }}>By account</div>
          <div style={{ fontSize: 12, color: INK_MUTED, marginBottom: 8 }}>
            The same selection, split by account. Click one to filter to it.
          </div>
          <table style={tableStyle}>
            <thead><tr>
              <Th>Account</Th><Th>Name</Th><Th align="right">Transactions</Th>
              <Th align="right">Credits</Th><Th align="right">Debits</Th>
              <Th align="right">Net</Th><Th align="right">Unposted</Th>
            </tr></thead>
            <tbody>
              {data.byAccount.map(a => {
                const on = account.length === 1 && account[0] === a.acct.slice(-4);
                return (
                  <tr key={a.acct} onClick={() => setAccount(on ? [] : [a.acct.slice(-4)])}
                      style={{ cursor: "pointer",
                               background: on ? "rgba(18,181,219,0.10)" : "transparent" }}>
                    <Td><code style={{ fontFamily: "var(--font-mono)" }}>{a.acct}</code></Td>
                    <Td>{a.accountname}</Td>
                    <Td align="right">{a.rows.toLocaleString("en-US")}</Td>
                    <Td align="right">{moneyFull(a.creditAmount)}</Td>
                    <Td align="right">{moneyFull(a.debitAmount)}</Td>
                    <Td align="right">{moneyFull(a.net)}</Td>
                    <Td align="right">{a.unposted || "—"}</Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      ) : null}

      {data && data.byMonth && data.byMonth.length > 1 ? (
        <Card style={{ marginTop: 12 }}>
          <div style={{ fontWeight: 600, color: INK_PRIMARY, marginBottom: 2 }}>By month</div>
          <div style={{ fontSize: 12, color: INK_MUTED, marginBottom: 8 }}>
            Credits above the line, debits below. Same selection.
          </div>
          <MonthBars rows={data.byMonth} />
        </Card>
      ) : null}

      <Card style={{ marginTop: 12 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 8,
                      flexWrap: "wrap" }}>
          <div style={{ fontWeight: 600, color: INK_PRIMARY }}>Transactions</div>
          <div style={{ fontSize: 12, color: INK_MUTED }}>
            {paging.total
              ? `${(paging.page * PAGE_SIZE + 1).toLocaleString("en-US")}–`
                + `${Math.min((paging.page + 1) * PAGE_SIZE, paging.total).toLocaleString("en-US")}`
                + ` of ${paging.total.toLocaleString("en-US")}`
              : "none"}
          </div>
          <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
            <Btn onClick={() => setPage(p => Math.max(0, p - 1))}
                 disabled={busy || paging.page === 0}>Previous</Btn>
            <Btn onClick={() => setPage(p => p + 1)} disabled={busy || !paging.more}>Next</Btn>
          </div>
        </div>

        {!rows.length && !busy ? (
          <div style={{ padding: 24, textAlign: "center", color: INK_MUTED, fontSize: 13 }}>
            Nothing matches this selection.
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={tableStyle}>
              <thead><tr>
                <Th />
                <SortTh id="date"    sort={sort} onSort={onSort}>Posted date</SortTh>
                <SortTh id="account" sort={sort} onSort={onSort}>Account</SortTh>
                <SortTh id="company" sort={sort} onSort={onSort}>Company</SortTh>
                <SortTh id="loan"    sort={sort} onSort={onSort}>Loan</SortTh>
                <SortTh id="type"    sort={sort} onSort={onSort}>Type</SortTh>
                <SortTh id="code"    sort={sort} onSort={onSort}>BAI code</SortTh>
                <SortTh id="amount"  sort={sort} onSort={onSort} align="right">Amount</SortTh>
                <SortTh id="status"  sort={sort} onSort={onSort}>Status</SortTh>
                <SortTh id="posted"  sort={sort} onSort={onSort}>Posted</SortTh>
                <Th>Reference</Th>
                <Th>Description</Th>
              </tr></thead>
              <tbody>
                {rows.map(r => {
                  const isOpen = expanded && expanded.id === r.id;
                  return [
                    <tr key={r.id} style={{ background: isOpen ? SURFACE_MUTED : "transparent" }}>
                      <Td>
                        <button type="button" onClick={() => toggleRow(r)}
                                title={isOpen ? "Hide detail" : "Show the full wire text"}
                                style={{ border: "none", background: "transparent",
                                         cursor: "pointer", color: B.cyan, fontSize: 12,
                                         padding: 0, width: 16 }}>
                          {isOpen ? "▾" : "▸"}
                        </button>
                      </Td>
                      <Td>{usDate(r.date)}{r.time
                        ? <span style={{ color: INK_MUTED }}> {r.time}</span> : null}</Td>
                      <Td><code style={{ fontFamily: "var(--font-mono)",
                                         fontSize: 12 }}>{r.acct}</code></Td>
                      <Td>{r.company || <span style={{ color: INK_MUTED }}>—</span>}</Td>
                      <Td>{r.loanNumber || <span style={{ color: INK_MUTED }}>—</span>}
                        {r.manualOverride ? <span style={badge} title="Loan set by hand on the Pmt Posting screen">manual</span> : null}</Td>
                      <Td>{r.type}</Td>
                      <Td>{r.baiLabel}</Td>
                      <Td align="right">
                        {/* The SIGN is the direction, spelled out. A column of
                            positive numbers where half are money leaving is the
                            single easiest way to misread this table. */}
                        <span style={{ fontFamily: "var(--font-mono)",
                                       color: r.direction === "DEBIT" ? INK_MUTED : INK_PRIMARY }}>
                          {r.direction === "DEBIT" ? "−" : ""}{moneyFull(r.amount)}
                        </span>
                      </Td>
                      <Td>{r.status}</Td>
                      <Td>{r.posted
                        ? <span style={{ color: INK_MUTED }}>posted</span>
                        : <span style={{ ...badge, marginLeft: 0 }}>unposted</span>}</Td>
                      <Td><code style={{ fontFamily: "var(--font-mono)", fontSize: 11 }}>
                        {r.reference || "—"}</code></Td>
                      {/* ── THE BANK'S OWN WORDS, CLAMPED ──
                          Wire text runs to a couple of hundred characters, so the
                          cell shows the first line's worth and the rest is on
                          hover. The inner div is doing the clamping rather than
                          the Td: Td is whiteSpace:nowrap for every column, and an
                          ellipsis needs a width to overflow against.

                          Already masked. getBankTransactions runs both text
                          columns through maskDigits() before they leave the
                          server, so no account number reaches the browser here —
                          the same guarantee the Acct column relies on. The
                          expander below still has the unmasked text, by id, one
                          row at a time, which is the only path that does.

                          Not sortable on purpose: the server's sort map has no
                          description key, and a header that takes a click and
                          does nothing is worse than one that never offered. */}
                      <Td>
                        <div title={r.description || undefined}
                             style={{ maxWidth: 360, overflow: "hidden",
                                      textOverflow: "ellipsis", whiteSpace: "nowrap",
                                      color: r.description ? INK_PRIMARY : INK_MUTED }}>
                          {r.description || "—"}
                        </div>
                      </Td>
                    </tr>,
                    isOpen ? (
                      <tr key={r.id + "-d"}>
                        <td colSpan={12} style={{ background: SURFACE_MUTED, padding: "10px 14px",
                                                  borderBottom: `1px solid ${HAIRLINE}` }}>
                          {expanded.busy ? <span style={{ color: INK_MUTED, fontSize: 12 }}>
                            Loading the full text…</span>
                           : expanded.err ? <ErrorMsg msg={expanded.err} />
                           : <Detail row={expanded.row} />}
                        </td>
                      </tr>
                    ) : null,
                  ];
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

// The masked grid shows enough to scan. This shows what the bank actually sent
// for one row, and says plainly that it is unmasked so nobody screenshots it
// without noticing.
const Detail = ({ row }) => {
  if (!row) return null;
  const F = ({ label, children }) => (children == null || children === "" ? null : (
    <div style={{ marginBottom: 6 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: INK_MUTED,
                    textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
      <div style={{ fontSize: 12, color: INK_PRIMARY, whiteSpace: "pre-wrap",
                    wordBreak: "break-word", fontFamily: "var(--font-mono)" }}>{children}</div>
    </div>
  ));
  return (
    <div>
      <div style={{ fontSize: 11, color: INK_MUTED, marginBottom: 8 }}>
        Full text as the bank sent it — counterparty names and account numbers are
        not masked here.
      </div>
      <div style={{ display: "grid", gap: 14,
                    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}>
        <div>
          <F label="Description">{row.transactiondescription}</F>
          <F label="Descriptive details">{row.descriptivedetails}</F>
        </div>
        <div>
          <F label="Account">{row.acct} · {row.accountname}</F>
          <F label="Unique transaction id">{row.uniquetransactionid}</F>
          <F label="UETR">{row.uetr}</F>
          <F label="Customer reference">{row.customerreferencenumber}</F>
          <F label="Bank reference">{row.bankreference}</F>
          <F label="Value date">{String(row.valuedate || "").slice(0, 10)}</F>
          <F label="Imported">{String(row.importeddate || "").slice(0, 19).replace("T", " ")}</F>
          <F label="Posted by">{row.postedby}</F>
        </div>
      </div>
    </div>
  );
};

// ── credits up, debits down, one month per slot ────────────────────────────
//
// A diverging bar rather than two lines: money in and money out are the same
// measure in opposite directions, and a shared zero line makes "did more leave
// than arrived" a glance instead of a subtraction. Debits are drawn downward
// for the same reason they carry a minus sign in the grid.
const MB = { h: 120, pad: { l: 64, r: 12, t: 10, b: 26 } };
const MonthBars = ({ rows }) => {
  const wrap = useRef(null);
  const [w, setW] = useState(720);
  const [hi, setHi] = useState(null);
  useEffect(() => {
    if (!wrap.current || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(es => setW(Math.max(320, es[0].contentRect.width)));
    ro.observe(wrap.current); return () => ro.disconnect();
  }, []);
  if (!rows.length) return null;

  const H = MB.h + MB.pad.t + MB.pad.b;
  const iw = w - MB.pad.l - MB.pad.r;
  const slot = iw / rows.length;
  const bw = Math.max(3, Math.min(rows.length <= 6 ? 46 : 22, slot - 8));
  const x = i => MB.pad.l + (i + 0.5) * slot;
  const max = Math.max(...rows.map(r => Math.max(num(r.creditAmount), num(r.debitAmount))), 1);
  const zero = MB.pad.t + MB.h / 2;
  const half = MB.h / 2;
  const y = v => (num(v) / max) * half;
  const stride = Math.max(1, Math.ceil(rows.length / 12));

  return (
    <div ref={wrap} style={{ position: "relative" }}>
      <svg width="100%" height={H} viewBox={`0 0 ${w} ${H}`} role="img"
           aria-label="Credits and debits by month"
           onMouseLeave={() => setHi(null)}>
        <text x={MB.pad.l - 8} y={zero - half + 4} textAnchor="end" fontSize="11"
              fill={INK_MUTED}>{moneyShort(max)}</text>
        <text x={MB.pad.l - 8} y={zero + 4} textAnchor="end" fontSize="11"
              fill={INK_MUTED}>0</text>
        <text x={MB.pad.l - 8} y={zero + half + 4} textAnchor="end" fontSize="11"
              fill={INK_MUTED}>{moneyShort(max)}</text>
        <line x1={MB.pad.l} x2={w - MB.pad.r} y1={zero - half} y2={zero - half}
              stroke={HAIRLINE} strokeWidth="1" />
        <line x1={MB.pad.l} x2={w - MB.pad.r} y1={zero + half} y2={zero + half}
              stroke={HAIRLINE} strokeWidth="1" />
        {rows.map((r, i) => (
          <g key={r.period} onMouseEnter={() => setHi(i)} opacity={hi === i ? 1 : 0.9}>
            <rect x={MB.pad.l + i * slot} y={MB.pad.t} width={slot} height={MB.h}
                  fill="transparent" />
            {/* A 1px spacer off the zero line, so a small bar is still a bar and
                the baseline is never buried by one. */}
            <rect x={x(i) - bw / 2} y={zero - y(r.creditAmount)} width={bw}
                  height={Math.max(1, y(r.creditAmount) - 1)} fill={CREDIT_FILL} rx="2" />
            <rect x={x(i) - bw / 2} y={zero + 1} width={bw}
                  height={Math.max(1, y(r.debitAmount) - 1)} fill={DEBIT_FILL} rx="2" />
          </g>
        ))}
        <line x1={MB.pad.l} x2={w - MB.pad.r} y1={zero} y2={zero}
              stroke={HAIRLINE_FIRM} strokeWidth="1" />
        {rows.map((r, i) => (i % stride === 0 || i === rows.length - 1 ? (
          <text key={r.period} x={x(i)} y={H - 8}
                textAnchor={i === rows.length - 1 ? "end" : "middle"}
                fontSize="11" fill={INK_MUTED}>{periodLabel(r.period)}</text>
        ) : null))}
      </svg>
      {hi !== null && rows[hi] ? (
        <div style={{ position: "absolute", left: `${(x(hi) / w) * 100}%`, top: 0,
                      transform: "translateX(-50%)", pointerEvents: "none",
                      background: "rgba(255,255,255,0.97)", border: `1px solid ${HAIRLINE_FIRM}`,
                      borderRadius: 6, padding: "6px 9px", fontSize: 12, color: INK_PRIMARY,
                      whiteSpace: "nowrap", boxShadow: "0 4px 14px rgba(0,0,0,0.12)" }}>
          <strong>{periodLabel(rows[hi].period)}</strong>
          <div style={{ color: CREDIT_FILL }}>In: {moneyFull(rows[hi].creditAmount)}
            {" "}· {rows[hi].credits}</div>
          <div style={{ color: DEBIT_FILL }}>Out: {moneyFull(rows[hi].debitAmount)}
            {" "}· {rows[hi].debits}</div>
          <div>Net: {moneyFull(rows[hi].net)}</div>
        </div>
      ) : null}
      <div style={{ display: "flex", gap: 14, marginTop: 6, fontSize: 12, color: INK_MUTED }}>
        <span><Swatch c={CREDIT_FILL} /> Credits (in)</span>
        <span><Swatch c={DEBIT_FILL} /> Debits (out)</span>
      </div>
    </div>
  );
};
// The two hues already validated on the analytics pages, used here for the one
// distinction this chart makes. Money in and money out are not a sequence, so
// they take two hues rather than two steps of one.
const CREDIT_FILL = "#0e93b1";
const DEBIT_FILL  = "#c07f00";
const Swatch = ({ c }) => (
  <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 2,
                 background: c, marginRight: 5 }} />
);

const Stat = ({ label, value, note }) => (
  <Card style={{ padding: "10px 14px" }}>
    <div style={{ fontSize: 12, color: INK_MUTED }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 600, color: INK_PRIMARY }}>{value}</div>
    {note ? <div style={{ fontSize: 11, color: INK_MUTED }}>{note}</div> : null}
  </Card>
);

const tableStyle = { width: "100%", borderCollapse: "collapse", fontSize: 13 };
const badge = { marginLeft: 6, fontSize: 10, padding: "1px 5px", borderRadius: 8,
                background: SURFACE_MUTED, color: INK_MUTED, whiteSpace: "nowrap" };

const Th = ({ children, align }) => (
  <th style={{ textAlign: align || "left", padding: "6px 8px", fontSize: 11,
               color: INK_MUTED, fontWeight: 600, textTransform: "uppercase",
               letterSpacing: "0.04em", whiteSpace: "nowrap",
               borderBottom: `1px solid ${HAIRLINE_FIRM}` }}>{children}</th>
);
const Td = ({ children, align, colSpan }) => (
  <td colSpan={colSpan} style={{ textAlign: align || "left", padding: "6px 8px",
      color: INK_PRIMARY, borderBottom: `1px solid ${HAIRLINE}`,
      whiteSpace: "nowrap" }}>{children}</td>
);
// SORTS ON THE SERVER, because the client only ever holds ONE PAGE. Sorting a
// hundred rows in the browser re-orders the window rather than choosing it, so
// "sort by amount" would show the hundred most recent arranged by amount — a
// different table from the one that was asked for, and a plausible-looking one.
const SortTh = ({ id, children, align, sort, onSort }) => {
  const on = sort.key === id;
  return (
    <th onClick={() => onSort(id)} title={`Sort by ${String(children).toLowerCase()}`}
        style={{ textAlign: align || "left", padding: "6px 8px", fontSize: 11,
                 color: on ? INK_PRIMARY : INK_MUTED, fontWeight: on ? 700 : 600,
                 textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap",
                 borderBottom: `1px solid ${HAIRLINE_FIRM}`, cursor: "pointer",
                 userSelect: "none" }}>
      {children}
      <span style={{ marginLeft: 4, fontSize: 9, opacity: on ? 1 : 0.35 }}>
        {on ? (sort.dir === "asc" ? "▲" : "▼") : "▲"}
      </span>
    </th>
  );
};

export default BankTransactionsPage;
